// ──────────────────────────────────────────────────────────────────
// templatesApi.js
//
// CRUD wrapper around the user_templates Supabase table.
//
// All methods throw on error — callers should wrap in try/catch.
// RLS is enforced server-side, so we don't need to pass user_id on
// SELECT/UPDATE/DELETE; Supabase filters by auth.uid() automatically.
// We DO pass user_id on INSERT because the RLS WITH CHECK clause
// verifies it matches auth.uid(). The insert would fail silently
// without it.
// ──────────────────────────────────────────────────────────────────

import { supabase } from "./supabase";

const TABLE = "user_templates";

// Fetch all of the current user's saved templates. Returns [] if none.
export const listUserTemplates = async () => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Save (upsert) a user's override of a global template. One row per
// (user_id, global_id) pair, enforced by the partial unique index.
export const saveGlobalOverride = async (globalId, fields) => {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user?.id) throw new Error("Not authenticated");

  const row = {
    user_id: auth.user.id,
    global_id: globalId,
    name: fields.name,
    icon: fields.icon || "📋",
    who: fields.who || "",
    goal: fields.goal || "",
    context: fields.context || "",
    instructions: fields.instructions || "",
    phone: (fields.phone || "").replace(/\D/g, "").slice(0, 10),
  };

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(row, { onConflict: "user_id,global_id" })
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Create a brand-new user template (global_id stays null).
export const createUserTemplate = async (fields) => {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user?.id) throw new Error("Not authenticated");
  if (!fields.name?.trim()) throw new Error("Name is required");

  const row = {
    user_id: auth.user.id,
    global_id: null,
    name: fields.name.trim(),
    icon: fields.icon || "📋",
    who: fields.who || "",
    goal: fields.goal || "",
    context: fields.context || "",
    instructions: fields.instructions || "",
    phone: (fields.phone || "").replace(/\D/g, "").slice(0, 10),
  };

  const { data, error } = await supabase
    .from(TABLE)
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Update an existing user template (either an override or a custom one).
// id is the row's uuid primary key.
export const updateUserTemplate = async (id, fields) => {
  const patch = {};
  // Whitelist the fields clients can edit — defense against accidental
  // updates to user_id, created_at, etc.
  for (const k of ["name", "icon", "who", "goal", "context", "instructions"]) {
    if (fields[k] !== undefined) patch[k] = fields[k];
  }
  if (fields.phone !== undefined) {
    patch.phone = String(fields.phone).replace(/\D/g, "").slice(0, 10);
  }

  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Delete by id. For global overrides, deletion means "revert to global default."
// For custom templates, deletion removes them entirely.
export const deleteUserTemplate = async (id) => {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw error;
  return true;
};

// ──────────────────────────────────────────────────────────────────
// Merge helper — combine global templates + user overrides + custom
// templates into a single list for UI rendering.
//
// Rules:
//   1. For each global template, check if the user has an override row.
//      If yes, replace the global's fields with the override (preserving
//      icon/name unless explicitly changed). If no, return the global.
//   2. Append all custom user templates (rows with global_id IS NULL).
//
// Returns a list of template objects in the same shape as GLOBAL_TEMPLATES,
// with two extra fields:
//   - userRowId: uuid of the user_templates row, or null for pristine globals
//   - isCustom:  true if it's a user-created template with no global parent
// ──────────────────────────────────────────────────────────────────
export const mergeTemplates = (globals, userRows) => {
  const overrideMap = new Map();
  const customs = [];
  for (const row of userRows) {
    if (row.global_id) overrideMap.set(row.global_id, row);
    else customs.push(row);
  }

  const merged = globals.map((g) => {
    const ov = overrideMap.get(g.id);
    if (!ov) return { ...g, userRowId: null, isCustom: false };
    return {
      id: g.id,
      name: ov.name || g.name,
      icon: ov.icon || g.icon,
      category: g.category,  // category stays from global
      who: ov.who,
      goal: ov.goal,
      context: ov.context,
      instructions: ov.instructions,
      phone: ov.phone || "",
      userRowId: ov.id,
      isCustom: false,
    };
  });

  for (const c of customs) {
    merged.push({
      id: `custom-${c.id}`,
      name: c.name,
      icon: c.icon || "📋",
      category: "My Templates",
      who: c.who,
      goal: c.goal,
      context: c.context,
      instructions: c.instructions,
      phone: c.phone || "",
      userRowId: c.id,
      isCustom: true,
    });
  }

  return merged;
};
